import { Creature } from "@/store/level/types";
import {DraggableCreaturePanel} from "@/scenes.ui/draggable-creature-panel";

export class CreatureOrderService {
    private static readonly STORAGE_KEY = 'creature_order';

    public static saveCreatureOrder(creatures: Creature[]): void {
        try {
            const orderMap = new Map<number, number>();
            
            creatures.forEach((creature, index) => {
                orderMap.set(creature.id, index);
            });
            
            const orderData = Object.fromEntries(orderMap);
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(orderData));
        } catch (error) {
            console.error('Error saving creature order to localStorage:', error);
        }
    }

    public static saveCreatureOrderMap(creatures: Map<number, any>): void {
        const orderMap = new Map<number, number>();

        creatures.forEach((panel, index) => {
            orderMap.set((panel as DraggableCreaturePanel).getCreature().id, index);
        });

        const orderData = Object.fromEntries(orderMap);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(orderData));
    }

    public static loadCreatureOrder(): Map<number, number> {
        try {
            const orderData = localStorage.getItem(this.STORAGE_KEY);
            if (!orderData) {
                return new Map();
            }
            
            const orderObject = JSON.parse(orderData);
            return new Map(Object.entries(orderObject).map(([key, value]) => [parseInt(key), value as number]));
        } catch (error) {
            console.error('Error loading creature order from localStorage:', error);
            return new Map();
        }
    }

    public static sortCreaturesByOrder(creatures: Creature[]): Creature[] {
        const orderMap = this.loadCreatureOrder();
        
        if (orderMap.size === 0) {
            return creatures;
        }

        const sortedCreatures = [...creatures];
        
        sortedCreatures.sort((a, b) => {
            const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });

        return sortedCreatures;
    }

    public static updateCreatureOrder(creatures: Creature[], fromIndex: number, toIndex: number): void {
        if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || 
            fromIndex >= creatures.length || toIndex >= creatures.length) {
            return;
        }

        const updatedCreatures = [...creatures];
        
        const [movedCreature] = updatedCreatures.splice(fromIndex, 1);
        updatedCreatures.splice(toIndex, 0, movedCreature);
        
        this.saveCreatureOrder(updatedCreatures);
    }

    public static clearCreatureOrder(): void {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing creature order from localStorage:', error);
        }
    }
} 